import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Done, Error, Info, Warning } from '@material-ui/icons'
import { useAppDialogStyles } from './use-app-dialog.styles'
import CloseIcon from '@material-ui/icons/Close'
import { AppDialogVariants } from '../../store/app-dialog/types'
import { SimpleFunc } from '../../models'
import Button from '@material-ui/core/Button'
import { drawerBackdropBlurStyle } from '../../shared/style-settings'

type IAppDialogProps = {
  onExit?: () => void
  title: string
  forceClose?: boolean
  isShow?: boolean
  showCloseButton?: boolean
  actions?: React.ReactNode | null
  variant?: string
  message?: string
  closeHandler?: SimpleFunc
  resetHandler?: () => void
  setIsShow?: (value: boolean) => void
  children?: React.ReactNode
}

export const AppDialog: React.FC<IAppDialogProps> = (
  props
): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const {
    onExit,
    title,
    actions,
    isShow,
    variant,
    message,
    showCloseButton,
    closeHandler,
    setIsShow,
  } = props
  const classes = useAppDialogStyles()

  useEffect(() => {
    isShow ? setOpen(true) : setOpen(false)
  }, [isShow])

  const onCloseHandler = (): void => {
    setIsShow && setIsShow(false)
    closeHandler && closeHandler()
  }

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      onExited={onExit}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        style: drawerBackdropBlurStyle,
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onCloseHandler}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {variant && (
          <div className={classes.dialogIcon}>
            <AppDialogIcon variant={variant} />
          </div>
        )}
        <Typography
          id="alert-dialog-description"
          variant="body1"
          color="textSecondary"
          component="div"
          className={classes.dialogMessage}
        >
          {message}
        </Typography>
      </DialogContent>
      {
        <DialogActions>
          {actions ? (
            actions
          ) : (
            <Button
              onClick={() => setIsShow && setIsShow(false)}
              variant="contained"
              color="primary"
            >
              Ok
            </Button>
          )}
        </DialogActions>
      }
    </Dialog>
  )
}

interface IVariantIconProps {
  variant?: string
}

const AppDialogIcon: React.FC<IVariantIconProps> = (
  props
): React.ReactElement => {
  const theme = useTheme()
  const { variant } = props

  switch (variant) {
    case AppDialogVariants.success:
      return <Done htmlColor={theme.palette.success.main} />

    case AppDialogVariants.error:
      return <Error htmlColor={theme.palette.error.main} />

    case AppDialogVariants.warning:
      return <Warning htmlColor={theme.palette.warning.main} />

    case AppDialogVariants.info:
      return <Info htmlColor={theme.palette.info.main} />

    default:
      console.warn(`Unknown app dialog variant icon: ${variant}`)
      return <></>
  }
}
